import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';

import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { RentalsAnalyticsComponent } from '../../../shared/components/analytics/rentals-analytics/rentals-analytics.component';
import { VehiclesAnalyticsComponent } from '../../../shared/components/analytics/vehicles-analytics/vehicles-analytics.component';
import { WalletAnalyticsComponent } from '../../../shared/components/analytics/wallet-analytics/wallet-analytics.component';
import { OperatorAnalyticsComponent } from '../operator-analytics/operator-analytics.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { OperatorsService } from '../../../shared/services/operators.service';
import {
  OPERATOR_STATUS_META,
  OperatorDashboardResponse,
} from '../../../shared/types/operator.types';
import { TPipe } from '../../../shared/pipe/t.pipe';

/**
 * Tableau de bord de l'opérateur courant (`GET /operators/me/dashboard`).
 *
 * Trois écrans selon l'état :
 *  - **404** (aucun opérateur lié au user) → carte « pas encore opérateur » +
 *    CTA candidature (`/apply`, module #67).
 *  - **PENDING / SUSPENDED / EXHAUSTED** → bandeau d'alerte contextuel en tête.
 *  - **ACTIVE** → KPIs (soldes wallet + prestataires + commissions du mois).
 *
 * Le bandeau EXHAUSTED renvoie vers le wallet pour recharger le CREDIT (module
 * #70) : c'est l'action qui débloque automatiquement le statut côté back.
 */
@Component({
  selector: 'app-operator-dashboard',
  standalone: true,
  imports: [TPipe, DecimalPipe, RouterLink, PageHeaderComponent, DataStateComponent, OperatorAnalyticsComponent, RentalsAnalyticsComponent, VehiclesAnalyticsComponent, WalletAnalyticsComponent],
  templateUrl: './operator-dashboard.component.html',
})
export class OperatorDashboardComponent {
  private readonly operators = inject(OperatorsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly statusMeta = OPERATOR_STATUS_META;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** true quand le back renvoie 404 : user authentifié mais sans opérateur lié. */
  readonly notAnOperator = signal(false);
  readonly data = signal<OperatorDashboardResponse | null>(null);

  readonly isPending = computed(() => this.data()?.operatorStatus === 'PENDING');
  readonly isActive = computed(() => this.data()?.operatorStatus === 'ACTIVE');
  readonly isExhausted = computed(() => this.data()?.operatorStatus === 'EXHAUSTED');
  readonly isSuspended = computed(() => this.data()?.operatorStatus === 'SUSPENDED');

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.notAnOperator.set(false);

    this.operators
      .myDashboard()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.data.set(res);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          // 404 = pas d'opérateur lié → écran candidature dédié, pas une erreur.
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this.notAnOperator.set(true);
            this.loading.set(false);
            return;
          }
          this.error.set(toErrorMessage(err, 'errors.dashboardLoadFailed'));
          this.loading.set(false);
        },
      });
  }
}
